import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Building2, Scale, CreditCard, Shield, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead } from '../components/SEOHead';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LegalNoticePage = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const ownerName = settings?.owner_name || 'Muller Stéphane';
  const companyName = settings?.company_legal_name || 'Muller Construction SRL';
  const companyVat = settings?.company_vat || 'BE 0803.324.504';
  const companyAddress = settings?.company_address || 'Belgique';
  const companyCountry = settings?.company_country || 'Belgique';
  const companyPostalCode = settings?.company_postal_code || '';
  const companyEmail = settings?.company_email || 'contact@buildconnect.be';
  const companyPhone = settings?.company_phone || '';
  const platformName = settings?.platform_name || 'BuildConnect';
  const jurisdiction = settings?.legal_jurisdiction || "Tribunaux de l'arrondissement de l'exploitant";
  const applicableLaw = settings?.applicable_law || 'Droit belge';
  const bankName = settings?.bank_name || '';
  const bankIban = settings?.bank_iban || '';
  const bankBic = settings?.bank_bic || '';
  const customContent = settings?.legal_notice_content;

  const fullAddress = [companyAddress, companyPostalCode, companyCountry].filter(Boolean).join(', ');
  const vatNumber = companyVat.replace(/\s/g, '').replace('BE', '');
  const bceUrl = `https://kbopub.economie.fgov.be/kbopub/toonondernemingps.html?ondernemingsnummer=${vatNumber}`;

  if (customContent) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="legal-notice-page">
        <SEOHead
          title="Mentions Légales"
          description="Mentions légales de BuildConnect - Muller Construction SRL. Informations juridiques, TVA, IBAN et conditions."
          path="/mentions-legales"
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"><Button variant="ghost" className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button></Link>
          <h1 className="font-heading font-bold text-3xl text-[#111827] mb-8">Mentions Légales</h1>
          <div className="prose prose-lg max-w-none text-[#374151] whitespace-pre-wrap" data-testid="legal-notice-custom-content">
            {customContent}
          </div>
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-[#6B7280]">
              <strong>Propriétaire :</strong> {ownerName} | <strong>Exploitant :</strong> {companyName}<br />
              <strong>Siège social :</strong> {fullAddress}<br />
              <strong>TVA :</strong> {companyVat}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="legal-notice-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/"><Button variant="ghost" className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button></Link>

        <h1 className="font-heading font-bold text-3xl text-[#111827] mb-2">Mentions Légales</h1>
        <p className="text-sm text-[#6B7280] mb-8">Informations légales conformes au droit belge et européen</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card data-testid="legal-owner-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <h2 className="font-heading font-bold text-lg text-[#111827]">Propriétaire du site</h2>
              </div>
              <div className="space-y-2 text-[#374151]">
                <p className="font-semibold text-[#111827]" data-testid="legal-owner-name">{ownerName}</p>
                <p className="text-sm">Propriétaire du nom de domaine buildconnect.be et de l'ensemble des contenus de la plateforme {platformName}.</p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="legal-company-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <h2 className="font-heading font-bold text-lg text-[#111827]">Exploitant</h2>
              </div>
              <div className="space-y-2 text-[#374151]">
                <p className="font-semibold text-[#111827]" data-testid="legal-company-name">{companyName}</p>
                <p className="text-sm">Siège social : {fullAddress}</p>
                {companyEmail && <p className="text-sm">Email : {companyEmail}</p>}
                {companyPhone && <p className="text-sm">Tél : {companyPhone}</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6" data-testid="legal-vat-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="font-heading font-bold text-lg text-[#111827]">Numéro de TVA / BCE</h2>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div>
                <p className="text-lg font-mono font-bold text-[#111827]" data-testid="legal-vat-number">{companyVat}</p>
                <p className="text-sm text-[#6B7280] mt-1">Vérifiable sur le portail officiel de la Banque-Carrefour des Entreprises (BCE)</p>
              </div>
              <a
                href={bceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-colors text-sm font-medium whitespace-nowrap"
                data-testid="legal-verify-bce-link"
              >
                <ExternalLink className="w-4 h-4" />
                Vérifier sur BCE
              </a>
            </div>
          </CardContent>
        </Card>

        {bankIban && (
          <Card className="mb-6" data-testid="legal-bank-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="font-heading font-bold text-lg text-[#111827]">Coordonnées bancaires</h2>
              </div>
              <div className="space-y-1 text-[#374151]">
                {bankName && <p className="text-sm"><span className="font-medium">Bénéficiaire :</span> {bankName}</p>}
                <p className="text-sm"><span className="font-medium">IBAN :</span> <span className="font-mono">{bankIban}</span></p>
                {bankBic && <p className="text-sm"><span className="font-medium">BIC/SWIFT :</span> <span className="font-mono">{bankBic}</span></p>}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6" data-testid="legal-jurisdiction-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center">
                <Scale className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              <h2 className="font-heading font-bold text-lg text-[#111827]">Droit applicable et juridiction</h2>
            </div>
            <div className="space-y-2 text-[#374151]">
              <p className="text-sm"><span className="font-medium">Droit applicable :</span> {applicableLaw}</p>
              <p className="text-sm"><span className="font-medium">Juridiction compétente :</span> {jurisdiction}</p>
              <p className="text-sm mt-3">
                Conformément au Règlement européen n° 524/2013, vous pouvez recourir à la plateforme de règlement en ligne des litiges :{' '}
                <a href="https://ec.europa.eu/consumers/odr" className="text-[#1E3A8A] underline" target="_blank" rel="noreferrer">
                  ec.europa.eu/consumers/odr
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-4 text-sm">
          <Link to="/terms" className="text-[#1E3A8A] hover:underline font-medium">Conditions Générales d'Utilisation</Link>
          <span className="text-[#D1D5DB]">|</span>
          <Link to="/privacy" className="text-[#1E3A8A] hover:underline font-medium">Politique de Confidentialité</Link>
        </div>
      </div>
    </div>
  );
};

export default LegalNoticePage;
